import { createFileRoute } from "@tanstack/react-router"
import type { SortingState } from "@tanstack/react-table"
import type {
  RunFacetsResponse,
  RunFeedPreviewColumn,
  RunFeedPreviewFreshness,
  RunFeedPreviewResponse,
  RunFeedPreviewRow,
  RunItemVerdictReason,
  RunOutputWarning,
  RunSetAsideItem,
  RunSkuSummary,
  RunStatusSummary,
  RunVerdictReasonTally,
} from "@nova-ai/shared"
import { PREVIEW_FRESHNESS } from "@nova-ai/shared"
import type { Dispatch, SetStateAction } from "react"
import { useState } from "react"

import { Button } from "../components/ui/button"
import { Callout } from "../components/ui/callout"
import { Combobox } from "../components/ui/combobox"
import { QueryState } from "../components/layout/query-state"
import { RunFeedRowDrawer } from "../components/runs/run-feed-row-drawer"
import { RunFeedPreviewTable } from "../components/runs/run-feed-preview-table"
import type { FeedRowKey } from "../components/runs/run-feed-preview-table"
import { RunStorefront } from "../components/runs/run-storefront"
import { RunCategoryFilters } from "../components/runs/run-category-filters"
import { FeedViewModeTabs, type FeedViewMode } from "../components/runs/run-feed-view-mode-tabs"
import { RunMetadata } from "../components/runs/run-metadata"
import { RunHeader } from "../components/runs/run-header"
import { RunPageTabs } from "../components/runs/run-page-tabs"
import { RUN_PAGE_TAB, validateRunPageSearch, type RunPageTab } from "../lib/run-page-tab"
import { RunValueRecovery } from "../components/runs/run-value-recovery"
import { RunPreviewFreshnessNotice } from "../components/runs/run-preview-freshness-notice"
import { previewFreshness } from "../lib/run-preview-freshness"
import { filterRunInspection, summarizeRunInspection } from "../lib/run-preview-inspection"
import {
  NokCohortTabs,
  RunVerdictTabs,
  type NokCohort,
  type VerdictFilter,
} from "../components/runs/run-verdict-tabs"
import { SearchInput } from "../components/ui/search-input"
import { useDebounce } from "../hooks/use-debounce"
import { cn } from "../lib/utils"
import {
  useRun,
  useRunFacets,
  useRunFeedPreview,
  useRunSetAside,
  useRunStatus,
} from "../services/use-runs"

// Page sizes for the feed-preview table. Each page inspects that many config
// SKUs server-side, so a big run starts at the smallest and grows on demand.
const FEED_PAGE_SIZES = [200, 500, 1000] as const
const COMPACT_FEED_MAX_CONFIGS = 12
const COMPACT_FEED_MIN_HEIGHT = 210
const COMPACT_FEED_MAX_HEIGHT = 560
const COMPACT_FEED_ROW_HEIGHT = 42
const COMPACT_FEED_CHROME_HEIGHT = 112
// Storefront chrome is just the panel's own padding + border — the storefront
// renders no header strip of its own.
const COMPACT_STOREFRONT_MAX_CONFIGS = 8
const COMPACT_STOREFRONT_ROW_HEIGHT = 58
const COMPACT_STOREFRONT_CHROME_HEIGHT = 36

// Merge the loaded pages into one preview the table/drawer can consume: rows
// concatenated, columns unioned by code (value-label maps merged, since each
// page only resolves labels for the codes it contains), counts summed.
export function mergeFeedPages(
  pages: RunFeedPreviewResponse[]
): RunFeedPreviewResponse | undefined {
  const first = pages[0]
  if (!first) {
    return undefined
  }
  if (pages.length === 1) {
    return first
  }
  const rows = [
    ...new Map(
      pages.flatMap((page) => page.rows).map((row) => [`${row.configSku}\0${row.simpleSku}`, row])
    ).values(),
  ]
  const verdictItems = [
    ...new Map(
      pages.flatMap((page) => page.verdictItems).map((item) => [item.configSku, item])
    ).values(),
  ]
  const columnsByCode = new Map<string, RunFeedPreviewColumn>()
  for (const page of pages) {
    for (const col of page.columns) {
      const existing = columnsByCode.get(col.code)
      if (!existing) {
        columnsByCode.set(col.code, { ...col, valueLabels: { ...col.valueLabels } })
      } else if (col.valueLabels) {
        existing.valueLabels = { ...existing.valueLabels, ...col.valueLabels }
      }
    }
  }
  const freshness = previewFreshness(pages) ?? first.freshness
  return {
    ...first,
    freshness,
    columns: [...columnsByCode.values()].map((col) =>
      col.valueLabels && Object.keys(col.valueLabels).length === 0
        ? { code: col.code, label: col.label }
        : col
    ),
    rows,
    rowCount: rows.filter((row) => !row.isPlaceholder).length,
    configSkuCount: verdictItems.length,
    // Build-readiness aggregates over every loaded page: blocked if any page
    // is, gaps concatenated, count summed.
    buildBlocked:
      freshness.kind !== PREVIEW_FRESHNESS.current || pages.some((page) => page.buildBlocked),
    gaps: pages.flatMap((page) => page.gaps),
    gapCount: pages.reduce((total, page) => total + page.gapCount, 0),
    verdictOnlyGaps: pages.flatMap((page) => page.verdictOnlyGaps),
    verdictOnlyGapCount: pages.reduce((total, page) => total + page.verdictOnlyGapCount, 0),
    dropped: pages.flatMap((page) => page.dropped),
    droppedCount: pages.reduce((total, page) => total + page.droppedCount, 0),
    warnings: mergeWarnings(pages),
    verdictItems,
    offersNeededCount: sumKnownPageCounts(pages.map((page) => page.offersNeededCount)),
    offersAlreadyListedCount: sumKnownPageCounts(
      pages.map((page) => page.offersAlreadyListedCount)
    ),
    // Per-config leaf maps are disjoint across pages (each page covers different
    // configs), so a shallow merge is exact. `roles` is stable, so `...first`.
    magentoCategoryByConfigSku: Object.assign(
      {},
      ...pages.map((page) => page.magentoCategoryByConfigSku)
    ),
    pagination: pages[pages.length - 1]?.pagination,
  }
}

function mergeWarnings(pages: RunFeedPreviewResponse[]): RunOutputWarning[] {
  const warningsByKey = new Map<string, RunOutputWarning>()
  for (const page of pages) {
    for (const warning of page.warnings) {
      warningsByKey.set(`${warning.code}\u0000${warning.message}`, warning)
    }
  }
  return [...warningsByKey.values()]
}

function sumKnownPageCounts(counts: readonly (number | null)[]): number | null {
  if (counts.some((count) => count === null)) {
    return null
  }
  return counts.reduce<number>((total, count) => total + (count ?? 0), 0)
}

function RunDetailIndex() {
  const { slug } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  // The header/gate reads the lightweight run detail (run + a server-computed
  // verdict summary), not the heavy /products payload — this page renders no
  // per-SKU product data, so pulling every SKU just to count configs and tally
  // verdicts was wasteful; those aggregates are computed server-side now.
  // Feed preview loads independently and is non-blocking: it returns a partial
  // render plus the gaps that would stop a real feed build (see
  // FeedBuildBlockedNotice), and only errors on genuine failures.
  const runDetail = useRun(slug)
  const canonicalStatus = useRunStatus(slug)
  const [pageSize, setPageSize] = useState<number>(FEED_PAGE_SIZES[0])
  // How the preview renders: a storefront grid/table that mirrors the marketplace
  // listing (the default — off titles/images are caught at a glance), or the raw
  // CSV feed table.
  const [viewMode, setViewMode] = useState<FeedViewMode>("grid")
  // Verdict tabs share inspected pages and filter canonical current verdicts.
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>("all")
  // NOK cohort sub-tab, meaningful only while NOK is active: Fixable / Excluded
  // (the non-parked split) or Set aside (the parked bucket). Defaults to Fixable
  // — the actionable work.
  const [nokCohort, setNokCohort] = useState<NokCohort>("fixable")
  // NOK-reason facet filter: narrows the active cohort to one verdict reason
  // server-side. Only meaningful on the Fixable / Excluded cohorts, so it's
  // cleared whenever the verdict tab or cohort changes.
  const [reasonFilter, setReasonFilter] = useState<string | null>(null)
  // SKU/name search: keystroke-responsive local input, debounced before it
  // hits the (server-side) query so each keypress doesn't refetch the run.
  // Matches the config SKU or the product name (see filterRunSkus).
  const [searchInput, setSearchInput] = useState("")
  const search = useDebounce(searchInput.trim(), 300)
  // Scan filters, all applied server-side before pagination so they slice the
  // whole run, not just the loaded grid. Category options come from run-wide
  // facets; "Gaps only" keeps configs with a fixable gap.
  const [marketplaceCategory, setMarketplaceCategory] = useState<string | null>(null)
  const [magentoCategoryLeaf, setMagentoCategoryLeaf] = useState<string | null>(null)
  const [gapsOnly, setGapsOnly] = useState(false)
  // The active NOK cohort view: whether the parked bucket is showing and which
  // (if any) cohort narrows the feed-preview server-side. Derived off-component
  // so the page body stays under the complexity bound.
  const { isSetAside } = deriveNokView(verdictFilter, nokCohort)
  const facets = useRunFacets(slug)
  const setAsideList = useRunSetAside(slug, isSetAside)
  const filteredSetAsideItems = filterSetAsideItems(setAsideList.data?.items ?? [], search)
  const feedPreview = useRunFeedPreview(slug, pageSize, undefined, {
    search,
    marketplaceCategory,
    magentoCategoryLeaf,
  })
  const { inspected, inspectionSummary, preview } = deriveInspectionView(
    feedPreview.data?.pages,
    runDetail.data?.skuSummary.total,
    { verdict: verdictFilter, cohort: nokCohort, reason: reasonFilter, gapsOnly },
    feedPreview.error
  )
  const recovery = feedPreview.recovery
  const pagination = preview?.pagination
  const [sorting, setSorting] = useState<SortingState>([])
  const [selectedRowKey, setSelectedRowKey] = useState<FeedRowKey | null>(null)

  const handleSelectRow = (row: FeedRowKey) => {
    setSelectedRowKey({ configSku: row.configSku, simpleSku: row.simpleSku })
  }

  const handleCloseDrawer = () => {
    setSelectedRowKey(null)
  }

  // Switching the All/OK/NOK tab reshuffles the shared feed views, so reset the
  // cohort-scoped reason filter and close any open drawer.
  const handleVerdictChange = (next: VerdictFilter) => {
    setVerdictFilter(next)
    setSelectedRowKey(null)
    setReasonFilter(null)
  }

  // Switching the NOK cohort re-scopes the reason facets, so drop the reason
  // filter and any open drawer with it.
  const handleCohortChange = (next: NokCohort) => {
    setNokCohort(next)
    setReasonFilter(null)
    setSelectedRowKey(null)
  }

  // Changing the reason filter reshuffles the list, so close any open drawer.
  const handleReasonChange = (reason: string | null) => {
    setReasonFilter(reason)
    setSelectedRowKey(null)
  }

  // Each scan filter reshuffles the visible set, so drop any selection keyed to
  // a row/config that may no longer be present.
  const handleMarketplaceCategoryChange = (value: string | null) => {
    setMarketplaceCategory(value)
    setSelectedRowKey(null)
  }

  const handleMagentoCategoryChange = (value: string | null) => {
    setMagentoCategoryLeaf(value)
    setSelectedRowKey(null)
  }

  const handleGapsOnlyChange = (value: boolean) => {
    setGapsOnly(value)
    setSelectedRowKey(null)
  }

  return (
    <QueryState isLoading={runDetail.isLoading} error={runDetail.error} isEmpty={!runDetail.data}>
      {runDetail.data && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <RunHeader
            run={runDetail.data.run}
            readiness={runReadiness(canonicalStatus.data)}
            canValidate={
              canonicalStatus.isSuccess &&
              recovery.canValidate &&
              inspected?.freshness.kind === PREVIEW_FRESHNESS.current
            }
            onValidated={recovery.onValidated}
          />

          <RunPageTabs
            slug={slug}
            configCount={runDetail.data.skuSummary.total}
            preparation={runDetail.data.run.preparation}
            metadata={<RunMetadata run={runDetail.data.run} />}
            tab={tab}
            onTabChange={(nextTab) =>
              void navigate({
                search: (previous) => ({ ...previous, tab: nextTab }),
                resetScroll: false,
              })
            }
            sharedWarnings={
              <RunSharedWarnings
                warnings={runDetail.data.warnings}
                tab={tab}
                freshness={recovery.freshness}
                needsValidation={recovery.needsValidation}
                error={feedPreview.error}
              />
            }
          >
            <RunValueRecovery
              originatingRunId={runDetail.data.run.id}
              marketplace={runDetail.data.run.marketplace}
              slug={slug}
              runProductCount={runDetail.data.skuSummary.total}
              freshness={recovery.freshness}
              needsValidation={recovery.needsValidation}
              error={feedPreview.error}
            />

            <section className="relative flex min-h-0 flex-1 flex-col">
              <RunControlBand
                summary={inspectionSummary}
                verdictFilter={verdictFilter}
                onVerdictChange={handleVerdictChange}
                nokCohort={nokCohort}
                onCohortChange={handleCohortChange}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                searchInput={searchInput}
                onSearchChange={setSearchInput}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                reasonFilter={reasonFilter}
                onReasonChange={handleReasonChange}
                facets={facets.data}
                marketplaceCategory={marketplaceCategory}
                onMarketplaceCategoryChange={handleMarketplaceCategoryChange}
                magentoCategoryLeaf={magentoCategoryLeaf}
                onMagentoCategoryChange={handleMagentoCategoryChange}
                gapsOnly={gapsOnly}
                onGapsOnlyChange={handleGapsOnlyChange}
              />

              <InspectionScopeNotice
                inspected={inspected}
                total={inspectionSummary.total}
                filtered={[search, marketplaceCategory, magentoCategoryLeaf].some(Boolean)}
              />

              <FeedContentHost
                viewMode={viewMode}
                error={feedPreview.error}
                isLoading={feedPreview.isLoading}
                preview={preview}
                selectedRowKey={selectedRowKey}
                sorting={sorting}
                onSortingChange={setSorting}
                onSelectRow={handleSelectRow}
              />

              <FeedLoadMoreOverlay
                isSetAside={false}
                preview={inspected}
                total={pagination?.totalConfigSkus}
                pageSize={pageSize}
                hasNextPage={feedPreview.hasNextPage}
                isFetchingNextPage={feedPreview.isFetchingNextPage}
                onLoadMore={() => void feedPreview.fetchNextPage()}
              />
            </section>

            {preview && (
              <FeedRowDrawerHost
                slug={slug}
                preview={preview}
                selectedRowKey={selectedRowKey}
                setAsideItems={filteredSetAsideItems}
                onClose={handleCloseDrawer}
                onNavigateRow={handleSelectRow}
              />
            )}
          </RunPageTabs>
        </div>
      )}
    </QueryState>
  )
}

function deriveInspectionView(
  pages: RunFeedPreviewResponse[] | undefined,
  savedTotal: number | undefined,
  filter: Parameters<typeof filterRunInspection>[1],
  error: Error | null
) {
  const merged = mergeFeedPages(pages ?? [])
  const inspected =
    merged && error && merged.freshness.kind === PREVIEW_FRESHNESS.current
      ? {
          ...merged,
          freshness: {
            kind: PREVIEW_FRESHNESS.unknown,
            reason: "inspection_inputs_changed" as const,
            message: "Preview refresh failed; current verdicts are unavailable.",
          },
        }
      : merged
  return {
    inspected,
    inspectionSummary: summarizeRunInspection(
      inspected,
      inspected?.pagination?.totalConfigSkus ?? savedTotal ?? 0
    ),
    preview: withFallbackRows(filterRunInspection(inspected, filter), "nok", []),
  }
}

function InspectionScopeNotice({
  inspected,
  total,
  filtered,
}: {
  inspected: RunFeedPreviewResponse | undefined
  total: number
  filtered: boolean
}) {
  return (
    <p className="px-1 pb-2 text-xs text-muted-foreground" aria-live="polite">
      {inspected?.freshness.kind === PREVIEW_FRESHNESS.current
        ? "Current page observations"
        : "Current verdicts unavailable"}

      {` · ${inspected?.verdictItems.length ?? 0} of ${total} products inspected`}

      {filtered ? " · Counts match search and category filters" : ""}

      {" · Product verdicts do not replace whole-run readiness"}
    </p>
  )
}

function runReadiness(status: RunStatusSummary | undefined) {
  return status?.readiness
}

function RunSharedWarnings({
  warnings,
  tab,
  freshness,
  needsValidation,
  error,
}: {
  warnings: readonly RunOutputWarning[]
  tab: RunPageTab
  freshness: RunFeedPreviewFreshness | undefined
  needsValidation: boolean
  error: Error | null
}) {
  return (
    <>
      {warnings.length > 0 && <RunWarningsNotice warnings={warnings} />}

      {tab === RUN_PAGE_TAB.workflow && (
        <RunPreviewFreshnessNotice
          freshness={freshness}
          needsValidation={needsValidation}
          error={error}
        />
      )}
    </>
  )
}

function RunWarningsNotice({ warnings }: { warnings: readonly RunOutputWarning[] }) {
  return (
    <Callout tone="warning" className="text-sm">
      {warnings.map((warning) => (
        <div key={`${warning.code}:${warning.message}`}>{warning.message}</div>
      ))}
    </Callout>
  )
}

// The active NOK cohort view derived from the verdict tab + cohort sub-tab:
// whether the parked (Set-aside) bucket is showing, and which of the two
// non-parked cohorts (if any) narrows the feed-preview server-side. Extracted so
// the page component stays under the complexity bound.
function deriveNokView(
  verdictFilter: VerdictFilter,
  nokCohort: NokCohort
): { isSetAside: boolean; feedCohort: "fixable" | "excluded" | undefined } {
  const cohort = verdictFilter === "nok" ? nokCohort : null
  return {
    isSetAside: cohort === "set-aside",
    feedCohort: cohort === "fixable" || cohort === "excluded" ? cohort : undefined,
  }
}

// The reason facets offered for the active NOK cohort, drawn from the run
// summary's histogram (which already excludes parked rows). Each facet's count
// is the CONFIG-COHORT count (fixableCount / excludedCount), NOT the reason's
// total — bucketed by the same hasFixableGap predicate the server's cohort grid
// filter uses, so a facet count always equals the `cohort=… & reason=…` grid.
// A reason with zero configs in the active cohort is dropped. The Set-aside
// cohort browses its parked grid directly, so it exposes no reason facets.
function cohortReasonFacets(
  summary: RunSkuSummary,
  verdictFilter: VerdictFilter,
  nokCohort: NokCohort
): RunVerdictReasonTally[] {
  if (verdictFilter !== "nok") {
    return []
  }
  if (nokCohort === "fixable") {
    return summary.reasons
      .filter((r) => r.fixableCount > 0)
      .map((r) => ({ reason: r.reason, fixable: r.fixable, count: r.fixableCount }))
  }
  if (nokCohort === "excluded") {
    return summary.reasons
      .filter((r) => r.excludedCount > 0)
      .map((r) => ({ reason: r.reason, fixable: r.fixable, count: r.excludedCount }))
  }
  return []
}

// The consolidated control band above the feed: the verdict filter, the NOK
// cohort sub-control (only when NOK is active), the feed view mode (Feed / Grid /
// Table — shared by every tab), then search + page size pinned right. The
// per-reason facets live behind the Filters button, scoped to the active cohort.
// One band instead of the old two-row toolbar, to give the feed more vertical
// room. Extracted so the page component stays under the complexity bound.
function RunControlBand({
  summary,
  verdictFilter,
  onVerdictChange,
  nokCohort,
  onCohortChange,
  viewMode,
  onViewModeChange,
  searchInput,
  onSearchChange,
  pageSize,
  onPageSizeChange,
  reasonFilter,
  onReasonChange,
  facets,
  marketplaceCategory,
  onMarketplaceCategoryChange,
  magentoCategoryLeaf,
  onMagentoCategoryChange,
  gapsOnly,
  onGapsOnlyChange,
}: {
  summary: RunSkuSummary
  verdictFilter: VerdictFilter
  onVerdictChange: (value: VerdictFilter) => void
  nokCohort: NokCohort
  onCohortChange: (value: NokCohort) => void
  viewMode: FeedViewMode
  onViewModeChange: (value: FeedViewMode) => void
  searchInput: string
  onSearchChange: (value: string) => void
  pageSize: number
  onPageSizeChange: (value: number) => void
  reasonFilter: string | null
  onReasonChange: (reason: string | null) => void
  facets: RunFacetsResponse | undefined
  marketplaceCategory: string | null
  onMarketplaceCategoryChange: (value: string | null) => void
  magentoCategoryLeaf: string | null
  onMagentoCategoryChange: (value: string | null) => void
  gapsOnly: boolean
  onGapsOnlyChange: (value: boolean) => void
}) {
  const isNok = verdictFilter === "nok"
  return (
    <div className="mb-3 flex shrink-0 flex-col gap-2 rounded-xl border border-border bg-card p-3 shadow-card">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <RunVerdictTabs summary={summary} value={verdictFilter} onChange={onVerdictChange} />

        {isNok && <NokCohortTabs summary={summary} value={nokCohort} onChange={onCohortChange} />}

        <FeedViewModeTabs value={viewMode} onChange={onViewModeChange} />

        <RunCategoryFilters
          facets={facets}
          marketplaceCategory={marketplaceCategory}
          onMarketplaceCategoryChange={onMarketplaceCategoryChange}
          magentoCategoryLeaf={magentoCategoryLeaf}
          onMagentoCategoryChange={onMagentoCategoryChange}
          gapsOnly={isNok ? null : gapsOnly}
          onGapsOnlyChange={onGapsOnlyChange}
          gapsCount={summary.nokFixable}
          reasonFacets={cohortReasonFacets(summary, verdictFilter, nokCohort)}
          selectedReason={reasonFilter}
          onReasonChange={onReasonChange}
        />

        <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center gap-3">
          <SearchInput
            value={searchInput}
            onChange={onSearchChange}
            placeholder="Search by SKU or name…"
            size="sm"
            className="w-44 sm:w-56"
          />

          <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
            <span>Page size</span>

            <Combobox
              aria-label="Page size"
              size="sm"
              className="w-24"
              value={String(pageSize)}
              options={FEED_PAGE_SIZES.map((size) => ({
                value: String(size),
                label: String(size),
              }))}
              searchPlaceholder="Search sizes…"
              emptyMessage="No sizes"
              onChange={(value) => {
                if (value !== undefined) {
                  onPageSizeChange(Number(value))
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// Floating "Load more" pill overlaying the bottom of the feed area, so
// pagination doesn't cost the feed a footer strip of vertical space. Hidden once
// every config is loaded, and on the parked Set-aside grid (not paginated).
// Extracted so the page component stays under the complexity bound.
function FeedLoadMoreOverlay({
  isSetAside,
  preview,
  total,
  pageSize,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  isSetAside: boolean
  preview: RunFeedPreviewResponse | undefined
  total: number | undefined
  pageSize: number
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  if (!preview || isSetAside || !hasNextPage) {
    return null
  }
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center">
      <Button
        size="sm"
        onClick={onLoadMore}
        disabled={isFetchingNextPage}
        className="pointer-events-auto gap-2 shadow-floating"
      >
        {isFetchingNextPage ? "Loading…" : `Load more (+${pageSize})`}

        <span className="font-normal tabular-nums text-muted-foreground">
          {`${preview.configSkuCount} of ${total ?? preview.configSkuCount}`}
        </span>
      </Button>
    </div>
  )
}

// The preview body below the tabs, wrapped in the flex container that lets it
// fill the remaining height. Every view — All, OK, and each NOK cohort — renders
// the SAME feed/grid/table views; the per-reason facets live behind the Filters
// button, and configs the build drops from the feed still render as fallback
// cards (see withFallbackRows). The whole-run feed-gap count lives in the
// header's gap badge, so it isn't repeated here. Extracted so the page component
// stays under the complexity bound.
function FeedContentHost({
  viewMode,
  error,
  isLoading,
  preview,
  selectedRowKey,
  sorting,
  onSortingChange,
  onSelectRow,
}: {
  viewMode: FeedViewMode
  error: Error | null
  isLoading: boolean
  preview: RunFeedPreviewResponse | undefined
  selectedRowKey: FeedRowKey | null
  sorting: SortingState
  onSortingChange: Dispatch<SetStateAction<SortingState>>
  onSelectRow: (row: FeedRowKey) => void
}) {
  const compactHeight = preview ? compactContentHeight(viewMode, preview.configSkuCount) : null

  return (
    <div
      className={cn("min-h-0", compactHeight === null ? "flex-1" : "shrink-0")}
      style={compactHeight === null ? {} : { height: compactHeight }}
    >
      <FeedContent
        viewMode={viewMode}
        error={error}
        isLoading={isLoading}
        preview={preview}
        selectedRowKey={selectedRowKey}
        sorting={sorting}
        onSortingChange={onSortingChange}
        onSelectRow={onSelectRow}
      />
    </div>
  )
}

function compactContentHeight(viewMode: FeedViewMode, configSkuCount: number): number | null {
  if (viewMode === "grid") {
    return null
  }
  if (viewMode === "feed") {
    if (configSkuCount > COMPACT_FEED_MAX_CONFIGS) {
      return null
    }
    return Math.max(
      COMPACT_FEED_MIN_HEIGHT,
      Math.min(
        COMPACT_FEED_MAX_HEIGHT,
        COMPACT_FEED_CHROME_HEIGHT + configSkuCount * COMPACT_FEED_ROW_HEIGHT
      )
    )
  }
  if (configSkuCount > COMPACT_STOREFRONT_MAX_CONFIGS) {
    return null
  }
  return Math.max(
    COMPACT_FEED_MIN_HEIGHT,
    Math.min(
      COMPACT_FEED_MAX_HEIGHT,
      COMPACT_STOREFRONT_CHROME_HEIGHT + configSkuCount * COMPACT_STOREFRONT_ROW_HEIGHT
    )
  )
}

function filterSetAsideItems(
  items: readonly RunSetAsideItem[],
  search: string
): readonly RunSetAsideItem[] {
  const needle = search.trim().toLowerCase()
  if (!needle) {
    return items
  }
  return items.filter((item) =>
    [item.configSku, item.name ?? "", item.reason].some((value) =>
      value.toLowerCase().includes(needle)
    )
  )
}

// One config that produces no buildable feed row but still needs a storefront
// card: its catalog-mirror name + image, and the reasons that flag it (surfaced
// as the card's `missingRequired` markers). Both the NOK and Set-aside tabs feed
// their configs through this shape.
type FallbackRowItem = {
  configSku: string
  name: string | null
  image: string | null
  reasons: readonly string[]
}

// Presentation-only rows keep every inspected parent reachable in all views.
// They never change emitted row, variant, or offer counts.
export function withFallbackRows(
  preview: RunFeedPreviewResponse | undefined,
  mode: "set-aside" | "nok" | "none",
  setAsideItems: readonly RunSetAsideItem[]
): RunFeedPreviewResponse | undefined {
  if (!preview) {
    return preview
  }
  if (mode === "set-aside") {
    return appendFallbackRows(
      preview,
      setAsideItems.map((item) => ({
        configSku: item.configSku,
        name: preview.freshness.kind === PREVIEW_FRESHNESS.current ? item.name : null,
        image: preview.freshness.kind === PREVIEW_FRESHNESS.current ? item.image : null,
        reasons: item.verdictReasons.map((reason) => reason.reason),
      }))
    )
  }
  if (mode === "nok") {
    return appendFallbackRows(
      preview,
      preview.verdictItems.map((item) => ({
        configSku: item.configSku,
        name: preview.freshness.kind === PREVIEW_FRESHNESS.current ? item.name : null,
        image: preview.freshness.kind === PREVIEW_FRESHNESS.current ? item.image : null,
        reasons: item.reasons.map((reason) => reason.reason),
      }))
    )
  }
  return preview
}

// Append a synthetic feed row for each fallback item not already in the built
// rows, so a config the build dropped still shows up as a card. The synthetic
// row carries only the title + image roles; the drawer fills in the rest from
// the per-SKU preview. When the built feed has no title/image columns (a NOK-only
// page can build zero rows, so zero columns), add them so the grid can resolve
// the role cells.
function appendFallbackRows(
  preview: RunFeedPreviewResponse,
  items: readonly FallbackRowItem[]
): RunFeedPreviewResponse {
  if (items.length === 0) {
    return preview
  }
  const existingConfigSkus = new Set(preview.rows.map((row) => row.configSku))
  const titleCode = preview.roles.title ?? "name"
  const imageCode = preview.roles.image ?? "image"
  const fallbackRows = items
    .filter((item) => !existingConfigSkus.has(item.configSku))
    .map(
      (item): RunFeedPreviewRow => ({
        configSku: item.configSku,
        simpleSku: item.configSku,
        values: {
          [titleCode]: item.name ?? item.configSku,
          ...(item.image ? { [imageCode]: item.image } : {}),
        },
        missingRequired: [...item.reasons],
        needsOffer: false,
        isPlaceholder: true,
      })
    )

  if (fallbackRows.length === 0) {
    return preview
  }

  const titleColumn =
    preview.roles.title && !preview.columns.some((column) => column.code === preview.roles.title)
      ? [{ code: preview.roles.title, label: "Name" }]
      : []
  const imageColumn =
    !preview.columns.some((column) => column.code === imageCode) &&
    fallbackRows.some((row) => row.values[imageCode])
      ? [{ code: imageCode, label: "Image" }]
      : []
  const rows = [...preview.rows, ...fallbackRows]
  return {
    ...preview,
    columns: [...imageColumn, ...titleColumn, ...preview.columns],
    rows,
    columnCount: preview.columns.length + titleColumn.length + imageColumn.length,
    roles: { ...preview.roles, image: preview.roles.image ?? imageCode },
  }
}

// The feed-preview body: a genuine error, the rendered table, or a loading
// placeholder. Extracted so the page component stays under the complexity bound.
export function FeedContent({
  viewMode,
  error,
  isLoading,
  preview,
  selectedRowKey,
  sorting,
  onSortingChange,
  onSelectRow,
}: {
  viewMode: FeedViewMode
  error: Error | null
  isLoading: boolean
  preview: RunFeedPreviewResponse | undefined
  selectedRowKey: FeedRowKey | null
  sorting: SortingState
  onSortingChange: Dispatch<SetStateAction<SortingState>>
  onSelectRow: (row: FeedRowKey) => void
}) {
  if (error && (!preview || preview.freshness.kind === PREVIEW_FRESHNESS.current)) {
    return <FeedBuildError error={error} />
  }
  if (preview && viewMode !== "feed") {
    return (
      <RunStorefront
        preview={preview}
        layout={viewMode}
        selectedRowKey={selectedRowKey}
        onSelectRow={onSelectRow}
      />
    )
  }
  if (preview) {
    return (
      <RunFeedPreviewTable
        preview={preview}
        isLoading={isLoading}
        error={preview.freshness.kind !== PREVIEW_FRESHNESS.current ? null : error}
        selectedRowKey={selectedRowKey}
        sorting={sorting}
        onSortingChange={onSortingChange}
        onSelectRow={onSelectRow}
      />
    )
  }
  return (
    <div className="rounded-md border border-border bg-card p-4 text-sm text-muted-foreground">
      Loading feed preview…
    </div>
  )
}

// Genuine preview failure (run not found, cache missing, …). Gaps no longer
// land here — they come back as a 200 with a partial build + the notice above.
export function FeedBuildError({ error }: { error: Error }) {
  return (
    <Callout tone="warning" className="text-sm">
      <div className="font-medium text-foreground">Feed preview unavailable</div>

      <div className="mt-1 text-muted-foreground">{error.message}</div>
    </Callout>
  )
}

export const Route = createFileRoute("/runs/$slug/")({
  validateSearch: validateRunPageSearch,
  component: RunDetailIndex,
})

function findSelectedRow(
  rows: readonly RunFeedPreviewRow[],
  selectedRowKey: FeedRowKey | null
): RunFeedPreviewRow | null {
  if (!selectedRowKey) {
    return null
  }

  return (
    rows.find(
      (row) =>
        row.configSku === selectedRowKey.configSku && row.simpleSku === selectedRowKey.simpleSku
    ) ?? null
  )
}

// Resolves the selected feed row + its config-SKU neighbours and renders the
// feed-row drawer. Extracted so the page component stays under the complexity
// bound (the row lookups live next to the drawer they feed). Every tab uses this
// one drawer; the config's verdict reasons (looked up from the page's verdict
// items) drive the "why this product is NOK" section for NOK/set-aside configs.
function FeedRowDrawerHost({
  slug,
  preview,
  selectedRowKey,
  setAsideItems,
  onClose,
  onNavigateRow,
}: {
  slug: string
  preview: RunFeedPreviewResponse
  selectedRowKey: FeedRowKey | null
  setAsideItems: readonly RunSetAsideItem[]
  onClose: () => void
  onNavigateRow: (row: FeedRowKey) => void
}) {
  const selectedRow = findSelectedRow(preview.rows, selectedRowKey)
  const { previousRow, nextRow } = findRowNeighbors(preview.rows, selectedRow)
  const setAsideInfo = setAsideItems.find((item) => item.configSku === selectedRow?.configSku)
  const verdictReasons = findVerdictReasons(preview.verdictItems, selectedRow?.configSku)
  const variantRows = selectedRow
    ? preview.rows.filter((row) => row.configSku === selectedRow.configSku)
    : []
  return (
    <RunFeedRowDrawer
      slug={slug}
      isHistorical={preview.freshness.kind !== PREVIEW_FRESHNESS.current}
      row={selectedRow}
      columns={preview.columns}
      variantRows={variantRows}
      previousRow={previousRow}
      nextRow={nextRow}
      setAsideInfo={setAsideInfo}
      verdictReasons={verdictReasons}
      verdictItem={preview.verdictItems.find((item) => item.configSku === selectedRow?.configSku)}
      dropped={preview.dropped.filter((drop) => drop.configSku === selectedRow?.configSku)}
      onClose={onClose}
      onNavigateRow={onNavigateRow}
    />
  )
}

// The persisted verdict reasons for the drawer's config, or empty when the
// config is OK / not classified. The drawer renders the fixable vs won't-list
// breakdown from these, so NOK products explain themselves the way the retired
// NOK drawer did.
function findVerdictReasons(
  verdictItems: RunFeedPreviewResponse["verdictItems"],
  configSku: string | undefined
): readonly RunItemVerdictReason[] {
  if (!configSku) {
    return []
  }
  return verdictItems.find((item) => item.configSku === configSku)?.reasons ?? []
}

function findRowNeighbors(
  rows: readonly RunFeedPreviewRow[],
  selectedRow: RunFeedPreviewRow | null
): { previousRow: RunFeedPreviewRow | null; nextRow: RunFeedPreviewRow | null } {
  if (!selectedRow) {
    return { previousRow: null, nextRow: null }
  }
  // Navigate per config SKU, not per variant row: a config's simples are
  // near-identical, so jumping variant-by-variant means many clicks to reach
  // the next product. The chevrons land on the first variant of the adjacent
  // config instead.
  const configSkus: string[] = []
  for (const row of rows) {
    if (!configSkus.includes(row.configSku)) {
      configSkus.push(row.configSku)
    }
  }
  const configIndex = configSkus.indexOf(selectedRow.configSku)
  if (configIndex < 0) {
    return { previousRow: null, nextRow: null }
  }
  const firstRowOfConfig = (configSku: string | undefined): RunFeedPreviewRow | null =>
    configSku === undefined ? null : (rows.find((row) => row.configSku === configSku) ?? null)
  return {
    previousRow: firstRowOfConfig(configSkus[configIndex - 1]),
    nextRow: firstRowOfConfig(configSkus[configIndex + 1]),
  }
}
